import imageDefinitions from './images.js';
import { setParallaxTranslation } from './parallaxMotion.js';
import { getMobileStandalone } from './mobileStandalone.js';

const imageModule = {
  update: null,
  init(definitions = imageDefinitions) {
    const container = document.querySelector('.pages-container');
    const strip = container?.querySelector('.pages-inner');
    if (!strip || !definitions.length) return;
    const desktop = matchMedia('(min-width: 768px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const dialog = document.createElement('dialog');
    dialog.className = 'image-module-modal';
    dialog.setAttribute('aria-label', 'Image viewer');
    const createButton = (label, text, className) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = className;
      button.setAttribute('aria-label', label);
      button.textContent = text;
      return button;
    };
    const close = createButton('Close image viewer', '\u00d7', 'image-module-modal__close');
    const previous = createButton('Previous image', '\u2190', 'image-module-modal__previous');
    const next = createButton('Next image', '\u2192', 'image-module-modal__next');
    const fullImage = document.createElement('img');
    const caption = document.createElement('p');
    caption.id = 'image-module-caption';
    dialog.setAttribute('aria-describedby', caption.id);
    dialog.append(close, previous, fullImage, next, caption);
    document.body.append(dialog);
    const entries = [];
    const modalEntries = [];
    let current = 0;
    let opener = null;
    const show = index => {
      current = index;
      const { definition } = modalEntries[current];
      fullImage.src = definition.fullSrc || definition.src;
      fullImage.alt = definition.alt;
      caption.textContent = definition.caption || definition.alt;
      previous.disabled = current === 0;
      next.disabled = current === modalEntries.length - 1;
      previous.hidden = next.hidden = modalEntries.length < 2;
    };
    close.addEventListener('click', () => dialog.close());
    previous.addEventListener('click', () => show(current - 1));
    next.addEventListener('click', () => show(current + 1));
    dialog.addEventListener('click', event => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('keydown', event => {
      event.stopPropagation();
      if (event.key === 'ArrowLeft' && current > 0) {
        event.preventDefault();
        show(current - 1);
      }
      if (event.key === 'ArrowRight' && current < modalEntries.length - 1) {
        event.preventDefault();
        show(current + 1);
      }
    });
    dialog.addEventListener('close', () => opener?.focus({ preventScroll: true }));
    for (const definition of definitions) {
      const page = Array.from(strip.querySelectorAll('.page'))
        .find(element => element.dataset.id === definition.page);
      if (!page || !definition.src || !definition.alt) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'image-module';
      button.disabled = definition.modal === false;
      button.setAttribute('aria-label', button.disabled ? definition.alt : `View image: ${definition.alt}`);
      if (!button.disabled) button.setAttribute('aria-haspopup', 'dialog');
      const image = document.createElement('img');
      image.src = definition.src;
      image.alt = definition.alt;
      image.loading = definition.loading || 'lazy';
      image.draggable = false;
      button.append(image);
      const graphic = definition.graphic;
      if (graphic?.src && ['top', 'bottom', 'left', 'right'].includes(graphic.location)) {
        const decoration = document.createElement('img');
        decoration.className = `image-module__graphic image-module__graphic--${graphic.location}`;
        decoration.src = graphic.src;
        decoration.alt = '';
        decoration.setAttribute('aria-hidden', 'true');
        decoration.draggable = false;
        button.append(decoration);
      }
      if (!button.disabled) {
        const index = modalEntries.length;
        modalEntries.push({ definition });
        button.addEventListener('click', () => {
          opener = button;
          show(index);
          dialog.showModal();
        });
      }
      const anchor = page.querySelector('.video-over-video-stage');
      (anchor || strip).append(button);
      entries.push({ definition, page, anchor, button, image });
    }
    const update = () => {
      const stripBounds = strip.getBoundingClientRect();
      const viewport = container.getBoundingClientRect();
      const strength = reducedMotion.matches ? 0 :
        Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--image-parallax-amount')) || 0;
      for (const { definition, page, anchor, button, image } of entries) {
        const useMobilePage = !desktop.matches &&
          !page.classList.contains('mobile-page-pan--enabled') && definition.mobile?.page != null;
        const targetPage = useMobilePage
          ? Array.from(strip.querySelectorAll('.page'))
            .find(element => element.dataset.id === String(definition.mobile.page)) || page
          : page;
        const canvas = targetPage.classList.contains('mobile-page-pan--enabled')
          ? targetPage.querySelector('.mobile-page-pan-canvas') : null;
        const desktopLayout = desktop.matches || Boolean(canvas);
        const standalone = !desktopLayout
          ? getMobileStandalone(container, definition.mobile, definition.page) : null;
        const localAnchor = canvas || standalone || targetPage.querySelector('.video-over-video-stage');
        if (button.parentElement !== (localAnchor || strip)) (localAnchor || strip).append(button);
        const settings = !desktopLayout ? { ...definition, ...definition.mobile } : definition;
        const bounds = (localAnchor || targetPage.querySelector('.filmstrip-stage') || targetPage).getBoundingClientRect();
        const percentage = (value, fallback) => Number.parseFloat(value ?? fallback) / 100;
        const mobileWidth = !desktopLayout && definition.mobile?.width != null;
        const mobileHeight = !desktopLayout && definition.mobile?.height != null;
        const heightSized = settings.height != null && (!mobileWidth || mobileHeight);
        const imageHeight = bounds.height * percentage(settings.height, 40);
        const imageWidth = heightSized && image.naturalWidth && image.naturalHeight
          ? imageHeight * image.naturalWidth / image.naturalHeight
          : bounds.width * percentage(settings.width, 40);
        const mobileLeft = !desktopLayout && definition.mobile?.left != null;
        const rightAligned = settings.right != null && !mobileLeft;
        const horizontalPosition = rightAligned
          ? bounds.width * (1 - percentage(settings.right, 0)) - imageWidth
          : bounds.width * percentage(settings.left, 0);
        button.style.left = `${localAnchor ? horizontalPosition : bounds.left - stripBounds.left + horizontalPosition}px`;
        const mobileTop = !desktopLayout && definition.mobile?.top != null;
        const bottomAligned = settings.bottom != null && !mobileTop;
        const verticalPosition = bottomAligned ? 1 - percentage(settings.bottom, 0) : percentage(settings.top, 0);
        button.style.top = `${localAnchor ? bounds.height * verticalPosition : bounds.top - stripBounds.top + bounds.height * verticalPosition}px`;
        button.style.transform = bottomAligned ? 'translateY(-100%)' : 'none';
        button.style.width = `${imageWidth}px`;
        const distance = desktopLayout ? viewport.left - bounds.left : viewport.top - bounds.top;
        const override = settings.parallax == null ? NaN : Number(settings.parallax);
        const imageStrength = reducedMotion.matches ? 0 : Number.isFinite(override) ? override : strength;
        const displacement = distance * imageStrength;
        const offset = Number.isFinite(override) ? displacement : Math.max(-80, Math.min(80, displacement));
        if (targetPage.classList.contains('video-over-video-page--active')) {
          setParallaxTranslation(button, 0, desktop.matches, true);
          button.dataset.sceneFrozen = 'true';
        } else {
          button.dataset.sceneFrozen = 'false';
          setParallaxTranslation(button, standalone ? 0 : offset, desktopLayout, reducedMotion.matches || Boolean(canvas) || Boolean(standalone));
        }
      }
    };
    this.update = update;
    for (const { image } of entries) image.addEventListener('load', update);
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    reducedMotion.addEventListener('change', update);
    const observer = new ResizeObserver(update);
    observer.observe(container);
    for (const { page } of entries) observer.observe(page);
    update();
  },
};

export default imageModule;